<template>
    <div class="app-text-editor" :class="{ disabled: data.disabled }">
        <textarea
            :id="inputFieldId"
            :name="name"
        />
    </div>
</template>

<script>
import { InputMixin } from './mixin/InputMixin.js';

export default {
    name: 'TextEditor',
    mixins: [InputMixin],
    data() {
        return {
            isEditorReady: false,
            syncingExternalValue: false,
        };
    },
    mounted() {
        setTimeout(() => {
            this.initComponent(this.value);
        }, 100);
    },
    beforeDestroy() {
        const editor = this.editor();

        if (editor && editor.data('summernote')) {
            editor.summernote('destroy');
        }
    },
    watch: {
        editorValue(value) {
            if (!this.isEditorReady) {
                return;
            }

            const normalizedValue = this.normalizeValue(value);
            const currentValue = this.normalizeEditorOutput(this.editor().summernote('code'));

            if (currentValue === this.normalizeEditorOutput(normalizedValue)) {
                return;
            }

            this.syncingExternalValue = true;
            this.setInitValue(normalizedValue);
            this.$nextTick(() => {
                this.syncingExternalValue = false;
            });
        },
    },
    computed: {
        editorValue() {
            return this.value;
        },
    },
    methods: {
        editor() {
            return $("#" + this.inputFieldId);
        },
        initComponent(initialValue) {
            const allowMedia = this.data.allowMedia !== false;
            const config = {
                placeholder: this.data.placeholder,
                dialogsInBody: true,
                height: this.data.height,
                minHeight: Math.min(this.data.height, 260),
                maxHeight: null,
                disableDragAndDrop: !allowMedia,
                disableResizeEditor: false,
                fontNames: [
                    'Arial',
                    'Arial Black',
                    'Comic Sans MS',
                    'Courier New',
                    'Georgia',
                    'Helvetica',
                    'Impact',
                    'Lucida Grande',
                    'Tahoma',
                    'Times New Roman',
                    'Trebuchet MS',
                    'Verdana'
                ],
                fontNamesIgnoreCheck: [
                    'Arial',
                    'Arial Black',
                    'Comic Sans MS',
                    'Courier New',
                    'Georgia',
                    'Helvetica',
                    'Impact',
                    'Lucida Grande',
                    'Tahoma',
                    'Times New Roman',
                    'Trebuchet MS',
                    'Verdana'
                ],
                fontSizes: ['8', '9', '10', '11', '12', '14', '16', '18', '24', '30', '36', '48'],
                lineHeights: ['1.0', '1.2', '1.4', '1.6', '1.8', '2.0'],
                styleTags: ['p', 'blockquote', 'pre', 'h2', 'h3', 'h4', 'h5'],
                toolbar: this.data.minimal === true ? [
                    ['style', ['bold', 'italic', 'underline']],
                    ['para', ['ul', 'ol']],
                    ['insert', allowMedia ? ['picture', 'link'] : ['link']],
                ] : [
                    ['style', ['style']],
                    ['font', ['bold', 'italic', 'underline', 'clear']],
                    ['fontname', ['fontname']],
                    ['fontsize', ['fontsize']],
                    ['color', ['color']],
                    ['para', ['ul', 'ol', 'paragraph', 'height']],
                    ['table', ['table']],
                    ['insert', allowMedia ? ['link', 'picture', 'video', 'hr'] : ['link', 'hr']],
                    ['view', ['fullscreen', 'codeview', 'help']],
                ],
                callbacks: {
                    onChange: () => {
                        if (!this.syncingExternalValue) {
                            this.input();
                        }
                    },
                    onPaste: (event) => {
                        if (!allowMedia) {
                            const clipboardItems = Array.from(event?.originalEvent?.clipboardData?.items || []);

                            if (clipboardItems.some((item) => item.type && item.type.startsWith('image/'))) {
                                event.preventDefault();
                            }
                        }
                    },
                },
            };

            if (allowMedia) {
                config.callbacks.onImageUpload = (files) => {
                    this.insertImages(files);
                };
            }

            if (Object.keys(this.data.textEditorHints).length) {
                config.hint = this.data.textEditorHints;
            }

            this.editor().summernote(config);
            this.isEditorReady = true;
            this.setInitValue(initialValue);
        },
        normalizeValue(value) {
            return typeof value === 'string' ? value : '';
        },
        normalizeEditorOutput(value) {
            const html = this.normalizeValue(value);
            const plainText = html
                .replace(/<p><br><\/p>/gi, '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/<br\s*\/?>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .trim();

            return plainText ? html : '';
        },
        setInitValue(initialValue) {
            this.editor().summernote('code', this.normalizeValue(initialValue));
        },
        input() {
            this.fieldValue = this.normalizeEditorOutput(this.editor().summernote('code'));
            this.$emit('input', this.fieldValue);
        },
        async insertImages(files = []) {
            const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));

            for (const file of imageFiles) {
                await this.insertImage(file);
            }
        },
        async insertImage(file) {
            if (this.data.imageUploadUrl) {
                try {
                    const payload = new FormData();
                    payload.append('image', file);

                    const response = await this.axiosPost({
                        url: this.data.imageUploadUrl,
                        data: payload,
                    });
                    const imageUrl = response?.data?.url;

                    if (imageUrl) {
                        this.editor().summernote('insertImage', imageUrl, file.name);
                        return;
                    }
                } catch (error) {
                    const message = error?.response?.data?.message || this.$t('image_upload_failed');
                    this.$toastr.e(message);
                    return;
                }
            }

            await this.insertImageAsDataUrl(file);
        },
        insertImageAsDataUrl(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();

                reader.onload = ({ target }) => {
                    this.editor().summernote('insertImage', target.result, file.name);
                    resolve();
                };

                reader.onerror = () => resolve();
                reader.readAsDataURL(file);
            });
        },
    },
}
</script>

<style lang="scss">
.app-text-editor {
    position: relative;

    .note-editor.note-frame {
        border: 1px solid var(--default-border-color);
        border-radius: 0.95rem;
        background: var(--form-control-bg);
        overflow: visible;
        position: relative;
    }

    .note-toolbar {
        background: linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.05), rgba(var(--brand-primary-rgb), 0.01));
        border-bottom: 1px solid var(--default-border-color);
        padding: 0.55rem 0.65rem 0.4rem;
        position: relative;
        z-index: 4;
    }

    .note-btn-group .note-btn {
        border-color: var(--default-border-color);
        background: var(--base-color);
        color: var(--default-font-color);
    }

    .note-btn-group .note-btn:hover,
    .note-btn-group .note-btn:focus,
    .note-btn-group .note-btn.active {
        background: rgba(var(--brand-primary-rgb), 0.1);
        color: var(--default-font-color);
    }

    .note-editing-area,
    .note-editor.note-frame .note-editable {
        background: var(--form-control-bg);
    }

    .note-editor.note-frame .note-editable {
        color: var(--default-font-color);
        line-height: 1.72;
        padding: 1rem 1.1rem;
        resize: vertical;
        min-height: 16rem;
    }

    .note-editor.note-frame .note-editable img {
        max-width: 100%;
        height: auto;
    }

    .note-editor.note-frame .note-statusbar {
        border-top: 1px solid var(--default-border-color);
        background: var(--base-color);
        position: relative;
        z-index: 3;
    }

    .note-popover .popover-content,
    .note-modal-content {
        background: var(--base-color);
        color: var(--default-font-color);
    }

    .note-editor .dropdown-menu,
    .note-dropdown-menu {
        z-index: 1085;
        min-width: 12rem;
        max-height: 18rem;
        overflow-x: hidden;
        overflow-y: auto;
    }

    .note-editor .dropdown-menu .dropdown-item,
    .note-dropdown-menu .dropdown-item {
        white-space: nowrap;
    }

    .note-modal-header,
    .note-modal-body,
    .note-modal-footer {
        background: var(--base-color);
        color: var(--default-font-color);
        border-color: var(--default-border-color);
    }

    .note-form-label,
    .note-modal-title {
        color: var(--default-font-color);
    }

    .note-input,
    .note-form-control {
        color: var(--default-font-color);
        background: var(--form-control-bg);
        border-color: var(--default-border-color);
    }

    .note-editor.note-frame.focused {
        border-color: var(--brand-primary);
    }

    &.disabled {
        opacity: 0.7;
        pointer-events: none;
    }
}
</style>
